import { defineStore, acceptHMRUpdate } from 'pinia'
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'

import { useAccountStore } from 'src/stores/account'
import { useTicketQuery } from 'src/composables/ticket/query/useTicketQuery'
import {
  EMPTY_TICKET,
  TICKET_CREATION_STEP_NAMES,
  COMPANY_VIEW_MODE,
  SEARCH_TAB_NAMES,
  TICKET_CREATION_STEPS,
} from 'src/services/consts/ticket-creation.const'
import {
  hasQuestionAnswer,
  buildQuestionAnswerMap,
  hasTicketUnsavedChanges,
} from 'src/services/helpers/ticket.helper'

export const useNewTicketStore = defineStore('new-ticket', () => {
  const route = useRoute()
  const accountStore = useAccountStore()

  const ticketToEdit = ref({ ...EMPTY_TICKET })

  // Search step
  const activeTab = ref(SEARCH_TAB_NAMES.PRODUCTS)
  const entitySearchResults = ref(null)
  const companyViewMode = ref(COMPANY_VIEW_MODE.LIST)

  // Request details step
  const errors = ref({})

  // Fetches full ticket details when editing an existing ticket
  const ticketId = computed(() => route.params.ticketId)
  const { ticket, isTicketLoading, ticketError, refetchTicket } = useTicketQuery(ticketId)

  const step = computed(() => {
    const stepFromQuery = route.params.ticketStep
    const stepExists = TICKET_CREATION_STEPS.find((step) => step.name === stepFromQuery)
    return stepExists ? stepFromQuery : TICKET_CREATION_STEP_NAMES.PRODUCT_SEARCH
  })
  const hasUnsavedChanges = computed(() => {
    return hasTicketUnsavedChanges(ticket.value, ticketToEdit.value, step.value)
  })
  const questionAnswerMap = computed(() => {
    return buildQuestionAnswerMap(accountStore.newTicketQuestions, ticketToEdit.value.answers)
  })
  const filesById = computed(() => {
    const files = ticket.value
      ? ticket.value.uploadedFiles || []
      : ticketToEdit.value.uploadedFiles || []
    const map = {}
    for (const file of files) {
      map[file.fileId] = file
    }
    return map
  })
  const requiredQuestions = computed(() =>
    Object.values(questionAnswerMap.value).filter((q) => q.required)
  )
  const hasAnsweredRequiredQuestions = computed(() => {
    if (requiredQuestions.value.length === 0) {
      return true
    }
    const answeredRequiredCount = requiredQuestions.value.filter((q) =>
      hasQuestionAnswer(q.answer)
    ).length
    return answeredRequiredCount === requiredQuestions.value.length
  })
  const selectedCompany = computed(() => {
    const ticket = ticketToEdit.value
    if (!ticket.companyId) return null

    return (
      entitySearchResults.value?.companies.find((c) => c.companyId === ticket.companyId) || null
    )
  })

  function fileById(fileId) {
    return filesById.value[fileId] || null
  }

  function setTicketFields(fields) {
    ticketToEdit.value = { ...ticketToEdit.value, ...fields }
  }
  function loadTicket(sourceTicket) {
    const companyId = ticketToEdit.value.companyId || sourceTicket.companyId
    const productId = ticketToEdit.value.productId || sourceTicket.productId
    ticketToEdit.value = {
      companyName: sourceTicket.companyName,
      companyId,
      productName: sourceTicket.productName,
      productId,
      companyWebsite: sourceTicket.companyWebsite || '',
      productWebsite: sourceTicket.productWebsite || '',
      answers: sourceTicket.answers || {},
      ticketId: sourceTicket.ticketId,
      ticketStatus: sourceTicket.ticketStatus,
      uploadedFiles: sourceTicket.uploadedFiles || [],
    }

    errors.value = {}
  }
  function resetTicket() {
    ticketToEdit.value = { ...EMPTY_TICKET }
    entitySearchResults.value = null
    errors.value = {}
    companyViewMode.value = COMPANY_VIEW_MODE.LIST
    activeTab.value = SEARCH_TAB_NAMES.PRODUCTS
  }

  function setCompanyViewMode(mode) {
    companyViewMode.value = mode
  }
  function setActiveTab(tab) {
    activeTab.value = tab
  }
  function setErrors(errorsObj) {
    errors.value = errorsObj
  }
  function clearError(questionId) {
    if (errors.value[questionId]) {
      errors.value = { ...errors.value, [questionId]: false }
    }
  }

  return {
    step,
    ticketToEdit,
    entitySearchResults,
    hasUnsavedChanges,
    errors,
    companyViewMode,
    activeTab,

    ticket,
    isTicketLoading,
    ticketError,
    refetchTicket,

    questionAnswerMap,
    filesById,
    fileById,
    requiredQuestions,
    hasAnsweredRequiredQuestions,
    selectedCompany,

    setTicketFields,
    loadTicket,
    resetTicket,
    setErrors,
    clearError,
    setCompanyViewMode,
    setActiveTab,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useNewTicketStore, import.meta.hot))
}
